import "@/App.css";
import "@/index.css";
import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import { ThemeProvider, useTheme } from "./lib/theme";
import { siteAPI } from "./lib/api";
import { applySeo, DEFAULT_DESCRIPTION, SITE_NAME } from "./lib/seo";
import Header from "./components/layout/Header";
import BreakingNews from "./components/layout/BreakingNews";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import AdminMobileBottomNav from "./components/layout/AdminMobileBottomNav";
import Home from "./pages/Home";

const Footer = lazy(() => import("./components/layout/Footer"));
const PwaInstallPrompt = lazy(() => import("./components/pwa/PwaInstallPrompt"));
const GlobalAdvertisingCards = lazy(() => import("./components/ads/GlobalAdvertisingCards"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const RadioPage = lazy(() => import("./pages/RadioPage"));
const PlansPage = lazy(() => import("./pages/PlansPage"));
const LoginPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.ResetPasswordPage })));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const VousReporter = lazy(() => import("./pages/VousReporter"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const NotFoundPage = lazy(() => import("./pages/ErrorPages").then((module) => ({ default: module.NotFoundPage })));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const AuthorsListPage = lazy(() => import("./pages/AuthorPage").then((module) => ({ default: module.AuthorsListPage })));
const AuthorDetailPage = lazy(() => import("./pages/AuthorPage").then((module) => ({ default: module.AuthorDetailPage })));
const GalleriesListPage = lazy(() => import("./pages/GalleryPage").then((module) => ({ default: module.GalleriesListPage })));
const GalleryDetailPage = lazy(() => import("./pages/GalleryPage").then((module) => ({ default: module.GalleryDetailPage })));
const AboutPage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.AboutPage })));
const NewsletterInfoPage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.NewsletterInfoPage })));
const AdvertisingPage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.AdvertisingPage })));
const PartnersPage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.PartnersPage })));
const PrivacyPage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.TermsPage })));
const CookiesPage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.CookiesPage })));
const ProfilePage = lazy(() => import("./pages/StaticPages").then((module) => ({ default: module.ProfilePage })));
const AdminLayout = lazy(() => import("./pages/admin/AdminDashboard").then((module) => ({ default: module.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard").then((module) => ({ default: module.AdminDashboard })));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminComments = lazy(() => import("./pages/admin/AdminComments"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminRadio = lazy(() => import("./pages/admin/AdminRadio"));
const AdminPolls = lazy(() => import("./pages/admin/AdminPolls"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminNewsletters = lazy(() => import("./pages/admin/AdminNewsletters"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminCitizenReports = lazy(() => import("./pages/admin/AdminCitizenReports"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminRSS = lazy(() => import("./pages/admin/AdminRSS"));
const AdminAuthors = lazy(() => import("./pages/admin/AdminAuthors"));
const AdminHomePreview = lazy(() => import("./pages/admin/AdminHomePreview"));
const AdminSections = lazy(() => import("./pages/admin/AdminSections"));

function RouteThemeSync() {
  const { pathname } = useLocation();
  const { setTheme } = useTheme();

  useEffect(() => {
    setTheme(pathname.startsWith("/admin") ? "dark" : "light");
  }, [pathname, setTheme]);

  return null;
}

function routeLabelFromSlug(slug) {
  return decodeURIComponent(String(slug || ""))
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getRouteSeo(pathname, search) {
  const query = new URLSearchParams(search || "");
  const searchTerm = query.get("q") || "";

  if (pathname.startsWith("/admin")) {
    return { title: `Administration | ${SITE_NAME}`, description: "Espace prive Makaya Enfo.", noindex: true };
  }

  if (["/connexion", "/inscription", "/mot-de-passe-oublie", "/esqueci-minha-senha", "/reinitialiser-mot-de-passe", "/redefinir-senha", "/profil", "/abonnement/succes"].some((path) => pathname.startsWith(path))) {
    return { title: `Espace lecteur | ${SITE_NAME}`, description: "Espace prive des lecteurs Makaya Enfo.", noindex: true };
  }

  if (pathname === "/recherche") {
    return {
      title: searchTerm ? `Recherche: ${searchTerm}` : "Recherche",
      description: "Recherche interne Makaya Enfo.",
      path: "/recherche",
      noindex: true,
    };
  }

  if (pathname.startsWith("/article/")) {
    return {
      title: "Article",
      description: DEFAULT_DESCRIPTION,
      path: pathname,
      type: "article",
    };
  }

  if (pathname.startsWith("/categorie/")) {
    const label = routeLabelFromSlug(pathname.split("/")[2]);
    return {
      title: label ? `${label} - Actualités` : "Rubrique",
      description: label
        ? `Retrouvez les derniers articles Makaya Enfo dans la rubrique ${label}.`
        : DEFAULT_DESCRIPTION,
      path: pathname,
    };
  }

  const staticSeo = {
    "/": {
      title: "Makaya Enfo - Actualités haïtiennes et internationales",
      description: DEFAULT_DESCRIPTION,
    },
    "/radio": {
      title: "Podcast et radio Makaya Enfo",
      description: "Regardez les podcasts, interviews et emissions video de Makaya Enfo.",
    },
    "/plans": {
      title: "Plans premium Makaya Enfo",
      description: "Accédez aux contenus premium, analyses et reportages exclusifs de Makaya Enfo.",
    },
    "/contact": {
      title: "Contact Makaya Enfo",
      description: "Contactez la redaction, le support ou l'equipe commerciale de Makaya Enfo.",
    },
    "/vous-reporter": {
      title: "Vous Reporter Makaya Enfo",
      description: "Envoyez vos informations, temoignages, photos et videos a la redaction de Makaya Enfo.",
    },
    "/evenements": {
      title: "Événements Makaya Enfo",
      description: "Suivez les événements, agendas et rendez-vous couverts par Makaya Enfo.",
    },
    "/auteurs": {
      title: "Auteurs Makaya Enfo",
      description: "Découvrez les auteurs, journalistes et contributeurs de Makaya Enfo.",
    },
    "/galeries": {
      title: "Galeries Makaya Enfo",
      description: "Parcourez les galeries photo et video de Makaya Enfo.",
    },
    "/a-propos": {
      title: "À propos de Makaya Enfo",
      description: "Makaya Enfo est une plateforme digitale d'information fondée pour connecter les communautés.",
    },
    "/newsletter": {
      title: "Newsletter Makaya Enfo",
      description: "Recevez les actualités essentielles de Makaya Enfo directement par email.",
    },
    "/publicite": {
      title: "Publicité sur Makaya Enfo",
      description: "Presentez vos campagnes et annonces publicitaires sur Makaya Enfo.",
    },
    "/partenaires": {
      title: "Partenaires Makaya Enfo",
      description: "Construisez un partenariat editorial, commercial ou institutionnel avec Makaya Enfo.",
    },
    "/politique-de-confidentialite": {
      title: "Politique de confidentialité Makaya Enfo",
      description: "Consultez la politique de confidentialité de Makaya Enfo.",
    },
    "/conditions": {
      title: "Conditions d'utilisation Makaya Enfo",
      description: "Consultez les conditions generales d'utilisation de Makaya Enfo.",
    },
    "/cookies": {
      title: "Cookies Makaya Enfo",
      description: "Informations sur l'utilisation des cookies et preferences de navigation de Makaya Enfo.",
    },
  };

  return staticSeo[pathname] || {
    title: "Page non trouvee",
    description: "Cette page Makaya Enfo est introuvable ou a été déplacée.",
    noindex: true,
  };
}

function RouteSeoSync() {
  const location = useLocation();

  useEffect(() => {
    applySeo({
      path: location.pathname,
      ...getRouteSeo(location.pathname, location.search),
    });
  }, [location.pathname, location.search]);

  return null;
}

function ScrollToTopOnRouteChange() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [pathname, search]);

  return null;
}

function SiteAppearanceSync() {
  useEffect(() => {
    let cancelled = false;
    let loaded = false;
    const loadSettings = () => {
      if (loaded) return;
      loaded = true;
      siteAPI.settings().then((response) => {
        if (cancelled) return;
        const headingFont = String(response.data?.heading_font || "").trim();
        if (headingFont) {
          document.documentElement.style.setProperty("--mk-heading-font", headingFont);
        }
      }).catch(() => {});
    };

    window.addEventListener("scroll", loadSettings, { once: true, passive: true });
    window.addEventListener("pointerdown", loadSettings, { once: true, passive: true });
    window.addEventListener("touchstart", loadSettings, { once: true, passive: true });
    window.addEventListener("keydown", loadSettings, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("scroll", loadSettings);
      window.removeEventListener("pointerdown", loadSettings);
      window.removeEventListener("touchstart", loadSettings);
      window.removeEventListener("keydown", loadSettings);
    };
  }, []);

  return null;
}

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-[#c4170c] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function DeferredPublicExtras() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return () => {};
    const show = () => setReady(true);

    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(show, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(id);
    }

    const id = window.setTimeout(show, 800);
    return () => window.clearTimeout(id);
  }, []);

  if (!ready) return null;

  return (
    <Suspense fallback={null}>
      <GlobalAdvertisingCards />
      <Footer />
      <PwaInstallPrompt />
    </Suspense>
  );
}

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <BreakingNews />
      <main className="min-h-[100dvh] pb-24 md:pb-0">{children}</main>
      <DeferredPublicExtras />
      <MobileBottomNav />
    </>
  );
}

function AdminShell() {
  return (
    <>
      <AdminLayout />
      <AdminMobileBottomNav />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <RouteThemeSync />
            <RouteSeoSync />
            <ScrollToTopOnRouteChange />
            <SiteAppearanceSync />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                <Route path="/article/:slug" element={<PublicLayout><ArticleDetail /></PublicLayout>} />
                <Route path="/categorie/:slug" element={<PublicLayout><CategoryPage /></PublicLayout>} />
                <Route path="/recherche" element={<PublicLayout><SearchPage /></PublicLayout>} />
                <Route path="/radio" element={<PublicLayout><RadioPage /></PublicLayout>} />
                <Route path="/plans" element={<PublicLayout><PlansPage /></PublicLayout>} />
                <Route path="/connexion" element={<PublicLayout><LoginPage /></PublicLayout>} />
                <Route path="/inscription" element={<PublicLayout><RegisterPage /></PublicLayout>} />
                <Route path="/mot-de-passe-oublie" element={<PublicLayout><ForgotPasswordPage /></PublicLayout>} />
                <Route path="/esqueci-minha-senha" element={<PublicLayout><ForgotPasswordPage /></PublicLayout>} />
                <Route path="/reinitialiser-mot-de-passe" element={<PublicLayout><ResetPasswordPage /></PublicLayout>} />
                <Route path="/redefinir-senha" element={<PublicLayout><ResetPasswordPage /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
                <Route path="/vous-reporter" element={<PublicLayout><VousReporter /></PublicLayout>} />
                <Route path="/abonnement/succes" element={<PublicLayout><SubscriptionSuccess /></PublicLayout>} />
                <Route path="/evenements" element={<PublicLayout><EventsPage /></PublicLayout>} />
                <Route path="/auteurs" element={<PublicLayout><AuthorsListPage /></PublicLayout>} />
                <Route path="/auteur/:slug" element={<PublicLayout><AuthorDetailPage /></PublicLayout>} />
                <Route path="/galeries" element={<PublicLayout><GalleriesListPage /></PublicLayout>} />
                <Route path="/galerie/:id" element={<PublicLayout><GalleryDetailPage /></PublicLayout>} />
                <Route path="/a-propos" element={<PublicLayout><AboutPage /></PublicLayout>} />
                <Route path="/newsletter" element={<PublicLayout><NewsletterInfoPage /></PublicLayout>} />
                <Route path="/publicite" element={<PublicLayout><AdvertisingPage /></PublicLayout>} />
                <Route path="/partenaires" element={<PublicLayout><PartnersPage /></PublicLayout>} />
                <Route path="/politique-de-confidentialite" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
                <Route path="/conditions" element={<PublicLayout><TermsPage /></PublicLayout>} />
                <Route path="/cookies" element={<PublicLayout><CookiesPage /></PublicLayout>} />
                <Route path="/profil" element={<PublicLayout><ProfilePage /></PublicLayout>} />

                <Route path="/admin" element={<AdminShell />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="articles" element={<AdminArticles />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="comments" element={<AdminComments />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="plans" element={<AdminPlans />} />
                  <Route path="radio" element={<AdminRadio />} />
                  <Route path="polls" element={<AdminPolls />} />
                  <Route path="banners" element={<AdminBanners />} />
                  <Route path="partners" element={<AdminPartners />} />
                  <Route path="newsletters" element={<AdminNewsletters />} />
                  <Route path="citizen-reports" element={<AdminCitizenReports />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="media" element={<AdminMedia />} />
                  <Route path="publicite" element={<AdminBanners />} />
                  <Route path="rss" element={<AdminRSS />} />
                  <Route path="authors" element={<AdminAuthors />} />
                  <Route path="preview-home" element={<AdminHomePreview />} />
                  <Route path="sections" element={<AdminSections />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>

                <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
